import { normaliserDetail } from './normaliserDetail';
import axios from 'axios';
import { toast } from 'sonner';

/**
 * Resolves the API base URL at runtime.
 * - On preview domain (same origin): uses REACT_APP_BACKEND_URL
 * - On custom domain (gembacoach.tmbsaas.com etc): uses relative URL
 *   to avoid cross-origin / CORS issues
 */
const resolveBaseUrl = () => {
  const envUrl = process.env.REACT_APP_BACKEND_URL || '';
  try {
    const backendHost = new URL(envUrl).hostname;
    const currentHost = window.location.hostname;
    // Same origin — use the env var directly
    if (currentHost === backendHost || currentHost === 'localhost') {
      return envUrl;
    }
    // Different origin (custom domain) — use relative path (same-origin)
    return '';
  } catch {
    return envUrl;
  }
};

const BASE_URL = resolveBaseUrl();

/** Full API path for direct axios calls: e.g. `${API_BASE}/auth/login` */
export const API_BASE = `${BASE_URL}/api`;

/** Backend root (for image/upload URLs): e.g. `${BACKEND_URL}/uploads/photo.jpg` */
export const BACKEND_URL = BASE_URL;

// Create axios instance with defaults
const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' }
});

// Request interceptor - add auth token
apiClient.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor - handle errors globally
apiClient.interceptors.response.use(
  response => response,
  error => {
    normaliserDetail(error);
    const status = error?.response?.status;
    const url = error?.config?.url || '';

    // Don't show toast for expected errors
    if (status === 401) {
      // Session expired - redirect to login
      if (!url.includes('/auth/login')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/';
      }
    } else if (status === 403) {
      // Permission denied - silent
    } else if (status === 404) {
      // Missing endpoint - silent (stub will handle)
      console.warn(`API 404: ${url}`);
    } else if (status === 429) {
      toast.error('Trop de requetes. Veuillez patienter.');
    } else if (status >= 500) {
      console.error(`API ${status}: ${url}`, error?.response?.data);
    } else if (!error?.response) {
      // Network error
      console.warn('Network error:', url);
    }

    return Promise.reject(error);
  }
);

// Safe API call wrapper - returns default on error
export const safeApiCall = async (method, url, data = null, defaultValue = null) => {
  try {
    const config = {};
    let response;
    if (method === 'get') {
      response = await apiClient.get(url, config);
    } else if (method === 'post') {
      response = await apiClient.post(url, data, config);
    } else if (method === 'put') {
      response = await apiClient.put(url, data, config);
    } else if (method === 'delete') {
      response = await apiClient.delete(url, config);
    }
    return response?.data ?? defaultValue;
  } catch {
    return defaultValue;
  }
};

export default apiClient;
